class FarloUiRevolverCarousel {
	constructor(element, options = {}) {
		this.element = element;
		this.slides = Array.from(element.children);
		this.currentIndex = 0;
		this.startX = 0;
		this.dragStartX = 0;
		this.dragEndX = 0;
		this.visibleSlides = 1;
		this.totalVisibleSets = 0;
		this.isDragging = false;
		this.autoplayInterval = null;

		this.options = {
			arrows: true,
			auto: false,
			autoDuration: 3000,
			fade: false,
			lazyLoad: false,
			loop: true,
			pagination: true,
			swipe: true,
			...options
		};

		this.initialiseRevolver();
		this.addEventListeners();

		this.handleKeyboardInput = this.handleKeyboardInput.bind(this);
	}

	initialiseRevolver() {
		if (this.element.classList.contains('farlo-ui-revolver-carousel--loaded')) {
			return;
		}

		this.track = document.createElement('div');
		this.track.className = 'farlo-ui-revolver-carousel__cylinder';
		this.element.classList.add('farlo-ui-revolver-carousel');
		this.element.classList.add('farlo-ui-revolver-carousel--loaded');

		this.slides.forEach((slide) => {
			slide.classList.add('farlo-ui-revolver-carousel__chamber');
			this.track.appendChild(slide);
		});

		this.element.appendChild(this.track);

		// Initialize the carousel
		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();
		this.updateCarousel();

		if (this.options.pagination) {
			this.createPagination();
		}

		if (this.options.arrows) {
			this.createArrows();
		}

		if (this.options.fade) {
			this.applyFadeEffect();
		}

		if (this.options.auto) {
			this.startAutoplay();
		}
	}

	createPagination() {
		let paginationContainer = this.element.querySelector('.farlo-ui-revolver-carousel__pagination');

		// If paginationContainer doesn't exist, create it
		if (!paginationContainer) {
			paginationContainer = document.createElement('div');
			paginationContainer.className = 'farlo-ui-revolver-carousel__pagination';
			this.element.appendChild(paginationContainer);
		}

		// Clear existing dots
		paginationContainer.innerHTML = '';

		for (let i = 0; i < this.totalVisibleSets; i += 1) {
			const dot = document.createElement('button');
			dot.className = 'farlo-ui-revolver-carousel__pagination-sightdot';
			dot.setAttribute('aria-label', `Group ${ i + 1 }`);
			dot.addEventListener('click', () => this.goToSlide(i));
			paginationContainer.appendChild(dot);
		}

		// Set the first dot as active if needed
		this.updatePaginationDots();
	}

	createArrows() {
		const prevArrow = document.createElement('button');
		prevArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--prev';
		prevArrow.setAttribute('aria-label', 'Previous Slide');
		prevArrow.innerText = '<';
		prevArrow.addEventListener('click', () => this.prevSlide());

		const nextArrow = document.createElement('button');
		nextArrow.className = 'farlo-ui-revolver-carousel__trigger farlo-ui-revolver-carousel__trigger--next';
		nextArrow.setAttribute('aria-label', 'Next Slide');
		nextArrow.innerText = '>';
		nextArrow.addEventListener('click', () => this.nextSlide());

		this.element.appendChild(prevArrow);
		this.element.appendChild(nextArrow);
	}

	calculateVisibleSlides() {
		if (this.slides.length > 0) {
			// Assuming the carousel container width is well-defined
			const carouselWidth = this.element.offsetWidth;
			// Calculate width of one slide (including margins)
			const slideStyle = window.getComputedStyle(this.slides[ 0 ]);
			const slideWidth = this.slides[ 0 ].offsetWidth + parseInt(slideStyle.marginLeft, 10) + parseInt(slideStyle.marginRight, 10);
			// Calculate the number of visible slides
			this.visibleSlides = Math.round(carouselWidth / slideWidth);
		}
	}

	updateTotalVisibleSets() {
		this.totalVisibleSets = Math.ceil(this.slides.length / this.visibleSlides);
	}

	restartAutoplay() {
		this.stopAutoplay();
		this.startAutoplay();
	}

	applyFadeEffect() {
		this.track.classList.add('farlo-ui-revolver-carousel__cylinder--smoke');
	}

	addEventListeners() {
		// Touch events
		this.element.addEventListener('touchstart', this.handleTouchStart.bind(this), false);
		this.element.addEventListener('touchend', this.handleTouchEnd.bind(this), false);

		// Add keyboard event listener
		this.element.setAttribute('tabindex', '0'); // Make the element focusable
		this.element.addEventListener('focus', () => {
			this.element.addEventListener('keydown', this.handleKeyboardInput);
		});
		this.element.addEventListener('blur', () => {
			this.element.removeEventListener('keydown', this.handleKeyboardInput);
		});

		if (this.options.swipe) {
			this.element.addEventListener('mousedown', this.startDrag.bind(this));
			this.element.addEventListener('mousemove', this.dragging.bind(this));
			this.element.addEventListener('mouseup', this.endDrag.bind(this));
			this.element.addEventListener('mouseleave', this.endDrag.bind(this));
		}

		// Resize event for responsive adjustments
		window.addEventListener('resize', () => {
			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.onResize();
			}, 250); // Adjust debounce time as needed
		});
	}

	handleKeyboardInput(event) {
		switch (event.key) {
			case 'ArrowLeft':
				this.prevSlide();
				break;
			case 'ArrowRight':
				this.nextSlide();
				break;
		}
	}

	handleTouchStart(event) {
		this.startX = event.touches[ 0 ].clientX;
	}

	handleTouchEnd(event) {
		const endX = event.changedTouches[ 0 ].clientX;

		if (this.startX - endX > 30) {
			this.nextSlide();
		} else if (this.startX - endX < -30) {
			this.prevSlide();
		}
	}

	// Drag Handlers
	startDrag(event) {
		event.preventDefault(); // Prevent default to enable drag
		this.isDragging = true;
		this.dragStartX = event.type.includes('mouse') ? event.pageX : event.touches[ 0 ].pageX;
	}

	dragging(event) {
		if (!this.isDragging) {
			return;
		}
		this.dragEndX = event.type.includes('mouse') ? event.pageX : event.touches[ 0 ].pageX;
	}

	endDrag(event) {
		if (!this.isDragging) {
			return;
		}

		this.isDragging = false;

		const delta = this.dragStartX - this.dragEndX;

		if (Math.abs(delta) > 30) {
			delta > 0 ? this.nextSlide() : this.prevSlide();
		}
	}

	onResize() {
		this.calculateVisibleSlides();
		this.updateTotalVisibleSets();

		// Reset currentIndex if it exceeds the new total number of sets
		if (this.currentIndex >= this.totalVisibleSets) {
			this.currentIndex = this.totalVisibleSets - 1;
		}

		// Update the carousel and pagination
		this.updateCarousel();

		if (this.options.pagination) {
			this.createPagination();
		}
	}

	goToSlide(index) {
		this.currentIndex = index;
		this.updateCarousel();
	}

	nextSlide() {
		// Move to the next set, or to the last set if we reach the end
		this.currentIndex = this.currentIndex < this.totalVisibleSets - 1 ? this.currentIndex + 1 : (this.options.loop ? 0 : this.currentIndex);
		this.updateCarousel();
	}

	prevSlide() {
		this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : (this.options.loop ? this.totalVisibleSets - 1 : this.currentIndex);
		this.updateCarousel();
	}

	startAutoplay() {
		this.autoplayInterval = setInterval(() => {
			this.nextSlide();
		}, this.options.autoDuration);
	}

	stopAutoplay() {
		clearInterval(this.autoplayInterval);
	}

	updatePaginationDots() {
		const dots = this.element.querySelectorAll('.farlo-ui-revolver-carousel__pagination-sightdot');

		dots.forEach((dot, index) => {
			dot.classList.toggle('farlo-ui-revolver-carousel__pagination-sightdot--active', index === this.currentIndex);
		});
	}

	updateCarousel() {
		if (this.options.auto) {
			this.restartAutoplay();
		}

		let transformIndex = this.currentIndex * this.visibleSlides;

		// For the last set, adjust the transform to show the last slides
		if (this.currentIndex === this.totalVisibleSets - 1 && this.slides.length % this.visibleSlides !== 0) {
			transformIndex = this.slides.length - this.visibleSlides;
		}

		const transformValue = -(transformIndex * 100 / this.visibleSlides);
		this.track.style.transform = `translateX(${ transformValue }%)`;

		this.slides.forEach((slide, index) => {
			slide.classList.toggle('farlo-ui-revolver__chamber--active', index === this.currentIndex);
			slide.setAttribute('aria-hidden', index !== this.currentIndex);
			if (this.options.fade) {
				slide.style.opacity = this.options.fade && index === this.currentIndex ? '1' : '0';
			}
		});

		this.updatePaginationDots();
	}
}

export default FarloUiRevolverCarousel;
